import { format, endOfWeek, endOfMonth } from "date-fns";
import { useApi } from "@/lib/api";
import { EventCard } from "@/components/EventCard";
import { Spinner, Empty } from "@/components/common";

const groupEvents = (events) => {
  const now = new Date();
  const weekEnd = endOfWeek(now);
  const monthEnd = endOfMonth(now);
  const groups = [];
  const add = (key, title, e) => {
    let g = groups.find((x) => x.key === key);
    if (!g) groups.push((g = { key, title, events: [] }));
    g.events.push(e);
  };
  [...events]
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    .forEach((e) => {
      const d = new Date(e.start_time);
      if (d <= weekEnd) add("this-week", "This week", e);
      else if (d <= monthEnd) add("this-month", "Later this month", e);
      else add(format(d, "yyyy-MM"), format(d, "MMMM yyyy"), e);
    });
  return groups;
};

const shortLabel = (g) => (g.key.includes("-") && g.key.length === 7 ? format(new Date(`${g.key}-01T00:00:00`), "MMM yyyy") : g.title);

const JumpBar = ({ groups }) => (
  <nav data-testid="home-jump-bar" className="mt-8 sticky top-16 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-3 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-stone-200/70">
    <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {groups.map((g) => (
        <button key={g.key} data-testid={`home-jump-${g.key}`}
          onClick={() => document.getElementById(`group-${g.key}`)?.scrollIntoView({ behavior: "smooth" })}
          className="shrink-0 px-4 py-2 rounded-full border border-stone-300 bg-white text-sm font-semibold hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors">
          {shortLabel(g)} <span className="ml-1 font-mono text-[10px] opacity-60">{g.events.length}</span>
        </button>
      ))}
    </div>
  </nav>
);

const Group = ({ g }) => (
  <section id={`group-${g.key}`} data-testid={`home-group-${g.key}`} className="scroll-mt-32">
    <div className="sticky top-[7.9rem] z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-3 bg-[#FAF8F5]/90 backdrop-blur-md flex items-baseline gap-3">
      <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight">{g.title}</h2>
      <span className="font-mono text-xs text-stone-500">{g.events.length} {g.events.length === 1 ? "event" : "events"}</span>
    </div>
    <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {g.events.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
    </div>
  </section>
);

export default function Home() {
  const { data: ev, isLoading, error } = useApi("/public/events?limit=200", { refetchInterval: 30000 });
  const events = ev?.events || [];
  const groups = groupEvents(events);
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-28 md:pb-16">
      <div className="rise max-w-3xl">
        <h1 data-testid="home-hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
          Incredible creators hosting <span className="text-[#FF5C00]">amazing events.</span>
        </h1>
        <p data-testid="home-hero-subtitle" className="mt-4 text-base sm:text-lg text-stone-600">Find an event and get a ticket</p>
      </div>
      {groups.length > 1 && <JumpBar groups={groups} />}
      <div className="mt-6 space-y-12">
        {isLoading ? <Spinner /> : error && events.length === 0 ? (
          <Empty title="Events are temporarily unavailable" testId="home-events-error">We'll keep trying automatically. Check back in a minute.</Empty>
        ) : events.length === 0 ? <Empty title="No upcoming events yet" /> : groups.map((g) => <Group key={g.key} g={g} />)}
      </div>
    </main>
  );
}
